import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
  LinearProgress,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import StarIcon from '@mui/icons-material/Star';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CmsTestimonial } from '../../types/cms';
import { WebsiteCmsService } from '../../services/websiteCmsService';
import { generateVideoThumbnail, formatFileSize } from '../../utils/mediaOptimizer';

interface TestimonialsManagerTabProps {
  testimonials: CmsTestimonial[];
  onSave: (item: CmsTestimonial) => void;
  onDelete: (id: string) => void;
}

export default function TestimonialsManagerTab({
  testimonials,
  onSave,
  onDelete,
}: TestimonialsManagerTabProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<CmsTestimonial | null>(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFileSize, setSelectedFileSize] = useState<number | null>(null);

  const handleOpenAdd = () => {
    setEditingItem({
      id: `test-${Date.now()}`,
      customerName: '',
      designation: 'Homeowner, Swarna Avsar',
      projectName: 'Swarna Avsar',
      projectSlug: 'swarna-avsar',
      videoUrl: '',
      thumbnailUrl: '',
      quote: '',
      featured: true,
      order: testimonials.length,
    });
    setUploadProgress(0);
    setSelectedFileSize(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (item: CmsTestimonial) => {
    setEditingItem({ ...item });
    setUploadProgress(0);
    setSelectedFileSize(null);
    setDialogOpen(true);
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingItem) return;

    setSelectedFileSize(file.size);
    setUploadingVideo(true);
    setUploadProgress(5);

    try {
      // 1. Auto-extract video thumbnail / poster frame in background
      let posterUrl = editingItem.thumbnailUrl || '';
      try {
        const thumbResult = await generateVideoThumbnail(file, 1.0);
        if (thumbResult) {
          posterUrl = await WebsiteCmsService.uploadMedia(
            thumbResult.file,
            editingItem.projectSlug || 'general',
            'testimonials/posters'
          );
        }
      } catch (thumbErr) {
        console.warn('Poster frame generation skipped:', thumbErr);
      }

      // 2. Upload video with live progress tracking
      const videoUrl = await WebsiteCmsService.uploadMedia(
        file,
        editingItem.projectSlug || 'general',
        'testimonials',
        (progress) => setUploadProgress(progress)
      );

      setEditingItem((prev) => (prev ? {
        ...prev,
        videoUrl,
        thumbnailUrl: posterUrl || prev.thumbnailUrl,
      } : null));
    } catch (err) {
      console.error('Video upload failed', err);
    } finally {
      setUploadingVideo(false);
      e.target.value = '';
    }
  };

  const handleSave = () => {
    if (!editingItem || !editingItem.customerName) return;
    onSave(editingItem);
    setDialogOpen(false);
    setEditingItem(null);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: '#ffffff', border: '1px solid #e0e0e0' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              Customer Video Testimonials
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Manage resident reviews, video testimonials, and featured homepage quotes with automatic poster frame generation.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            sx={{ bgcolor: '#9d6f32', '&:hover': { bgcolor: '#805926' }, textTransform: 'none', fontWeight: 700 }}
          >
            Add Testimonial
          </Button>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 3,
        }}
      >
        {testimonials.map((t) => (
          <Card key={t.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2.5, border: '1px solid #e0e0e0' }}>
            <Box sx={{ position: 'relative', height: 180, bgcolor: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {t.videoUrl ? (
                <video
                  src={t.videoUrl}
                  poster={t.thumbnailUrl}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  preload="metadata"
                />
              ) : (
                <PlayCircleOutlineIcon sx={{ fontSize: 48, color: '#fff' }} />
              )}
              <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                {t.featured && (
                  <Box sx={{ bgcolor: '#9d6f32', color: '#fff', px: 1.5, py: 0.5, borderRadius: 1.5, fontSize: '0.75rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <StarIcon sx={{ fontSize: 14 }} /> Featured
                  </Box>
                )}
              </Box>
            </Box>

            <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                {t.customerName}
              </Typography>
              <Typography variant="body2" sx={{ color: '#9d6f32', fontWeight: 600, mb: 1 }}>
                {t.designation} • {t.projectName}
              </Typography>
              {t.quote && (
                <Typography variant="body2" sx={{ color: '#555', fontStyle: 'italic', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  "{t.quote}"
                </Typography>
              )}
            </CardContent>

            <CardActions sx={{ p: 2, pt: 0, justifyContent: 'flex-end', borderTop: '1px solid #f0f0f0' }}>
              <IconButton size="small" onClick={() => handleOpenEdit(t)} sx={{ color: '#9d6f32' }}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => onDelete(t.id)} sx={{ color: '#d32f2f' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </CardActions>
          </Card>
        ))}
      </Box>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingItem?.id.startsWith('test-') ? 'Add Testimonial' : 'Edit Testimonial'}
        </DialogTitle>
        <DialogContent dividers>
          {editingItem && (
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              <TextField
                label="Customer Name"
                fullWidth
                size="small"
                value={editingItem.customerName}
                onChange={(e) => setEditingItem({ ...editingItem, customerName: e.target.value })}
                required
              />
              <TextField
                label="Designation / Label (e.g. Homeowner, Wing A)"
                fullWidth
                size="small"
                value={editingItem.designation}
                onChange={(e) => setEditingItem({ ...editingItem, designation: e.target.value })}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Associated Project</InputLabel>
                <Select
                  label="Associated Project"
                  value={editingItem.projectSlug || 'swarna-avsar'}
                  onChange={(e) => setEditingItem({
                    ...editingItem,
                    projectSlug: e.target.value,
                    projectName: e.target.value === 'swarna-avsar' ? 'Swarna Avsar' : 'Swarna Lagom'
                  })}
                >
                  <MenuItem value="swarna-avsar">Swarna Avsar</MenuItem>
                  <MenuItem value="swarna-lagom">Swarna Lagom</MenuItem>
                </Select>
              </FormControl>

              {/* Video File Uploader */}
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', mb: 1, display: 'block', textTransform: 'uppercase' }}>
                  Customer Video Review (MP4 / MOV / WebM)
                </Typography>

                {selectedFileSize && selectedFileSize > 40 * 1024 * 1024 && (
                  <Alert severity="warning" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
                    Notice: File size is {formatFileSize(selectedFileSize)}. For optimal visitor mobile speeds, videos under 30MB are recommended.
                  </Alert>
                )}

                {editingItem.videoUrl ? (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
                    <Box sx={{ mb: 1.5, borderRadius: 1.5, overflow: 'hidden', bgcolor: '#000', maxHeight: 180 }}>
                      <video
                        src={editingItem.videoUrl}
                        poster={editingItem.thumbnailUrl}
                        controls
                        style={{ width: '100%', maxHeight: 180, display: 'block' }}
                      />
                    </Box>
                    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircleIcon sx={{ fontSize: 16 }} /> Video & Auto-Poster Saved
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button
                          component="label"
                          size="small"
                          variant="outlined"
                          startIcon={uploadingVideo ? <CircularProgress size={14} /> : <VideoFileIcon />}
                          disabled={uploadingVideo}
                          sx={{ textTransform: 'none', borderColor: '#9d6f32', color: '#9d6f32' }}
                        >
                          Replace Video
                          <input type="file" accept="video/mp4,video/quicktime,video/webm,video/m4v" hidden onChange={handleVideoUpload} />
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="text"
                          onClick={() => setEditingItem({ ...editingItem, videoUrl: '', thumbnailUrl: '' })}
                          sx={{ textTransform: 'none' }}
                        >
                          Remove
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                ) : (
                  <Paper
                    component="label"
                    variant="outlined"
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      borderStyle: 'dashed',
                      borderColor: '#9d6f32',
                      bgcolor: '#fdfbf9',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#f7f1e7' },
                    }}
                  >
                    <input type="file" accept="video/mp4,video/quicktime,video/webm,video/m4v" hidden onChange={handleVideoUpload} />
                    {uploadingVideo ? (
                      <Box sx={{ width: '100%', textAlign: 'center', py: 1 }}>
                        <Typography variant="body2" sx={{ color: '#9d6f32', fontWeight: 700, mb: 1 }}>
                          Uploading to Firebase Storage: {uploadProgress}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={uploadProgress}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: '#f0e6d9',
                            '& .MuiLinearProgress-bar': { bgcolor: '#9d6f32' },
                            mb: 1,
                          }}
                        />
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Auto-capturing cover poster frame at 1s...
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <CloudUploadIcon sx={{ fontSize: 36, color: '#9d6f32', mb: 1 }} />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#9d6f32' }}>
                          Click to Upload Customer Review Video
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#888' }}>
                          MP4, MOV, WebM • Auto-extracts cover poster frame & optimizes for fast playback
                        </Typography>
                      </>
                    )}
                  </Paper>
                )}
              </Box>

              <TextField
                label="Direct Video URL / Embed (Optional fallback)"
                fullWidth
                size="small"
                value={editingItem.videoUrl}
                onChange={(e) => setEditingItem({ ...editingItem, videoUrl: e.target.value })}
                helperText="Paste direct MP4 or YouTube link if preferred over file upload"
              />

              <TextField
                label="Quote / Review text"
                fullWidth
                multiline
                rows={3}
                size="small"
                value={editingItem.quote}
                onChange={(e) => setEditingItem({ ...editingItem, quote: e.target.value })}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editingItem.featured}
                    onChange={(e) => setEditingItem({ ...editingItem, featured: e.target.checked })}
                    sx={{ '& .Mui-checked': { color: '#9d6f32' } }}
                  />
                }
                label="Feature on Homepage Testimonials Banner"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#666', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={uploadingVideo}
            sx={{ bgcolor: '#9d6f32', '&:hover': { bgcolor: '#805926' }, textTransform: 'none', fontWeight: 700 }}
          >
            Save Testimonial
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
